import { TextField, TextFieldProps, Typography } from "@mui/material";
import { useController } from "react-hook-form";

import { useLanguageState } from "context/LanguageContext";
import { EMPTY_TEXT } from "providers/LanguageProvider";

import { FormControl } from "../FormControl/FormControl";
import { useDescriptionField } from "../hooks/useDescriptionField";
import { useFormContext } from "../hooks/useFormContext";

import { useFormInputTextStyles } from "./FormInputText.styles";

interface IProps extends Omit<TextFieldProps<"outlined">, "variant"> {
	name: string;
	containerClassName?: string;
	label?: string;
	isHideErrorField?: boolean;
	isHideError?: boolean;
	count?: number;
}

export const FormInputText = ({
	name,
	placeholder,
	label: defaultLabel,
	containerClassName,
	disabled,
	fullWidth = true,
	isHideErrorField,
	isHideError,
	count,
	required,
	...props
}: IProps) => {
	const { classes, cx } = useFormInputTextStyles();
	const { control } = useFormContext();
	const { getTranslate } = useLanguageState();

	const {
		field: { value, onChange, onBlur, ref },
		fieldState: { invalid },
	} = useController({ name, control });

	const { meta, label, required: schemaRequired } = useDescriptionField(name);

	const fieldLabel = defaultLabel || label;
	const fieldPlaceholder = placeholder || meta?.placeholder;
	const placeholderText =
		getTranslate(fieldPlaceholder) === EMPTY_TEXT
			? fieldPlaceholder
			: getTranslate(fieldPlaceholder);

	return (
		<FormControl
			name={name}
			disabled={disabled}
			className={containerClassName}
			fullWidth={fullWidth}
			isHideErrorField={isHideErrorField}
			isHideError={isHideError}
		>
			<TextField
				{...props}
				id={name}
				size="small"
				variant="outlined"
				required={schemaRequired || required}
				label={getTranslate(fieldLabel) === EMPTY_TEXT ? fieldLabel : getTranslate(fieldLabel)}
				placeholder={fieldPlaceholder ? placeholderText : undefined}
				onChange={event => {
					if (count && event.target.value.length > count) return;
					onChange(event.target.value);
				}}
				onBlur={onBlur}
				value={value ?? ""}
				name={name}
				inputRef={ref}
				error={invalid}
				InputProps={{
					endAdornment: count && (
						<Typography className={cx(classes.count, props.multiline && classes.countMultiline)}>
							{value ? String(value).length : 0}/{count}
						</Typography>
					),
					className: cx(
						!props.multiline ? classes.inputField : classes.inputMultiline,
						props.InputProps?.className,
					),
					classes: { ...props.InputProps?.classes, input: classes.input },
				}}
			/>
		</FormControl>
	);
};
